import Filters from '../Filters/Filters';
import FontAwesome from 'utils/FA';
import FunnelDateSelector from 'components/reporting/FunnelDateSelector';
import FunnelReportTable from 'components/reporting/FunnelReportTable';
import Placeholder from 'components/Placeholder';
import React, { useEffect } from 'react';
import useParams from '../../utils/useParams';
import withFiltering from '../../hocs/withFiltering';
import { compose } from 'redux';
import { currentCustomer, dependentDuck, duckify, filterDuck } from 'utils/duckify';
import { faPercentage } from '@fortawesome/pro-light-svg-icons/faPercentage';
import { faTable } from '@fortawesome/pro-light-svg-icons/faTable';
import { combinedFunnelReport, fields as fieldsDuck, modals, schoolYears as schoolYearsDuck } from 'ducks';
import './CombinedFunnelReport.scss';
import queryString from 'query-string';

const validateFilters = ({ field, values, errors }) => {
  if (field.solr_prompt === 'report_year') {
    if (!values || values.length === 0) {
      errors[field.solr_prompt] = 'must be selected';
    } else if (values.length > 5) {
      errors[field.solr_prompt] = 'must have 5 or fewer selections';
    }
  }
};

// Default year selection for the report: the current enrollment year and the previous year
const defaultYears = (customer, schoolYears) => {
  const defaultYears = [customer.current_enrollment_year.start_year];
  const beforeYears = schoolYears.map(sy => sy.start_year).filter(y => y < defaultYears[0]);

  if (beforeYears.length) {
    defaultYears.push(beforeYears[beforeYears.length - 1]);
  }

  return defaultYears;
};

const CombinedFunnelReport = (props) => {
  const {
    customer,
    schoolYears,
    exportReport,
    fields,
    filters,
    setFilters,
    location,
    funnelReport = []
  } = props;

  const [params, setParams] = useParams(location);
  const reportType = params.reportType || 'count';
  const asOfDate = params.asOfDate;

  const filterFields = [{
    display_name: 'Term',
    solr_prompt: 'report_year',
    options: schoolYears.map(sy => [sy.name, sy.start_year.toString()])
  }, ...fields];

  const defaultSelected = {
    report_year: defaultYears(customer, schoolYears).map(y => y.toString())
  };

  useEffect(() => {
    if (!schoolYears.ready || (filters.report_year && filters.report_year.length !== 0)) {
      return;
    }

    setFilters({ ...filters, ...defaultSelected }, { replace: true });
  }, [defaultSelected]);

  return (
    <div className="reporting">
      <div className="funnel-report-header">
        <div>
          <h2 className="inline">
            Funnel Report{' '}
            {schoolYears.ready
              && filters.report_year
              && filters.report_year
                .map(sy => sy) // make a copy
                .sort((a, b) => b - a) // sort descending
                .map(startYear => schoolYears.find(sy => sy.start_year === parseInt(startYear)).name)
                .join(', ')}
          </h2>

        </div>
        <div>
          <button
            onClick={() => exportReport({
              reportType,
              filters,
              asOfDate
            })}
            className="btn btn-success"
          >
            Export To CSV
          </button>

          <div className="btn-group report-type-toggle" role="group">
            <button
              type="button"
              className={reportType === 'count' ? 'btn btn-active' : 'btn btn-light'}
              onClick={() => setParams({reportType: 'count'})}
            >
              <span className="visually-hidden">Counts</span>
              <FontAwesome icon={faTable}/>
            </button>
            <button
              type="button"
              className={reportType === 'percentage' ? 'btn btn-active' : 'btn btn-light'}
              onClick={() => setParams({reportType: 'percentage'})}
            >
              <span className="visually-hidden">Percentages</span>
              <FontAwesome icon={faPercentage}/>
            </button>
          </div>
        </div>
      </div>

      <FunnelDateSelector
        date={asOfDate}
        setDate={date => setParams({ asOfDate: date })}
        loading={!funnelReport.ready}
      />

      <div className="funnel-report-filters">
        {fields.ready && fields.length > 0 && (
          <Filters
            fields={filterFields}
            selected={filters}
            defaultSelected={defaultSelected}
            submitFilters={setFilters}
            validate={validateFilters}
            loading={!funnelReport.ready}
          />
        )}
      </div>
      <Placeholder.loading ready={fields.ready}>
        <FunnelReportTable
          reportType={reportType}
          filters={filters}
          asOfDate={asOfDate}
          funnelReport={funnelReport}
          customer={customer}
        />
      </Placeholder.loading>
    </div>
  );
};

const mapDispatchToProps = (dispatch) => ({
  // eslint-disable-next-line camelcase
  exportReport: ({ reportType, filters, asOfDate }) => dispatch(modals.actions.show('Downloader', {
    url: '/api/reporting/combined_funnel_report/export',
    async: true,
    params: { report_type: reportType, filter: filters, as_of_date: asOfDate }
  }))
});
const mapDucksToProps = ({ filters, location }) => {
  const params = queryString.parse(location.search);
  const funnelReportDucks = filters && filters.report_year
    ? {funnelReport: filterDuck(combinedFunnelReport, { filter: filters, as_of_date: params.asOfDate })}
    : {};


  return {
    customer: currentCustomer,
    fields: dependentDuck(
      currentCustomer,
      ({ value: customer }) =>
        filterDuck(fieldsDuck, { solr_prompt: ['grade_id', ...customer.custom_filters_by_name] })
    ),
    schoolYears: schoolYearsDuck,
    ...funnelReportDucks
  };
};
const mapStateToProps = () => ({});


export default compose(
  withFiltering,
  duckify(mapDucksToProps, mapStateToProps, mapDispatchToProps)
)(CombinedFunnelReport);
